import { Button, Input, NoticeBase, SelectNew, TextArea } from "@synthesis/shared-ui";
import { IconPlus } from "@synthesis/shared-icons";
import { useFormContext, useWatch } from "react-hook-form";

import { CROSSING_UTILITY_STRUCTURE_TYPE_OPTIONS } from "../../../../constants";
import { useCreateApplicationDictionariesContext } from "../../create-application-dictionaries-context";
import {
  createDefaultCrossingCommunication,
  createDefaultCrossingIntersection,
  toSelectItems,
  type CrossingObjectDataFormValues,
} from "../../create-application-page.lib";
import { CreateApplicationFormField, CreateApplicationFormRow, CreateApplicationFormSection } from "../create-application-form-layout";
import {
  CrossingObjectBlockHeader,
  CrossingObjectFormField,
  CrossingObjectOptionalLabel,
} from "./create-application-crossing-object-data-form-controls";

export const CrossingObjectEngineeringSection = () => {
  const { dictionaries } = useCreateApplicationDictionariesContext();
  const methods = useFormContext<CrossingObjectDataFormValues>();
  const communications = useWatch<CrossingObjectDataFormValues, "communications">({ name: "communications" }) ?? [];

  const appendCommunication = () => {
    methods.setValue("communications", [...communications, createDefaultCrossingCommunication()], { shouldDirty: true, shouldValidate: false });
  };

  const removeCommunication = (index: number) => {
    methods.setValue(
      "communications",
      communications.filter((_, itemIndex) => itemIndex !== index),
      { shouldValidate: true },
    );
  };

  const appendIntersection = (communicationIndex: number) => {
    const communication = communications[communicationIndex];

    if (!communication) {
      return;
    }

    methods.setValue(
      `communications.${communicationIndex}.intersections`,
      [...(communication.intersections ?? []), createDefaultCrossingIntersection()],
      { shouldDirty: true, shouldValidate: false },
    );
  };

  const removeIntersection = (communicationIndex: number, intersectionIndex: number) => {
    const communication = communications[communicationIndex];

    if (!communication) {
      return;
    }

    methods.setValue(
      `communications.${communicationIndex}.intersections`,
      (communication.intersections ?? []).filter((_, itemIndex) => itemIndex !== intersectionIndex),
      { shouldValidate: true },
    );
  };

  return (
    <CreateApplicationFormSection title="Инженерные коммуникации">
      <NoticeBase
        status="info"
        titleAppearance="plain"
        title="Укажите сведения об инженерной коммуникации и желаемом месте её пересечения с инфраструктурой ОАО «РЖД»."
        description="Если объект включает несколько коммуникаций или предусматривает несколько мест пересечения, добавьте их отдельно. Если сведения о станции, перегоне, километре или пикетаже отсутствуют, достаточно обозначить место на ситуационном плане."
        className="max-w-none"
      />

      <div className="flex flex-wrap gap-4">
        <Button type="button" theme="secondary" size="sm" leftIcon={<IconPlus className="size-5" />} onClick={appendCommunication}>
          Добавить коммуникацию
        </Button>
      </div>

      {communications.map((communication, communicationIndex) => (
        <div key={`communication-${communicationIndex}`} className="flex flex-col gap-4">
          <CrossingObjectBlockHeader
            title={`Инженерная коммуникация №${communicationIndex + 1}`}
            action={
              <Button
                type="button"
                theme="secondary"
                size="sm"
                leftIcon={<IconPlus className="size-5" />}
                onClick={() => appendIntersection(communicationIndex)}
              >
                Добавить место пересечения
              </Button>
            }
            onRemove={communications.length > 1 ? () => removeCommunication(communicationIndex) : undefined}
          />

          <CreateApplicationFormRow>
            <CreateApplicationFormField label="Вид инженерной коммуникации" required>
              <SelectNew
                controlled
                controlledClear
                name={`communications.${communicationIndex}.utilityStructureType`}
                placeholder="Выберите из списка"
                items={toSelectItems(CROSSING_UTILITY_STRUCTURE_TYPE_OPTIONS)}
                required
                size="md"
                isSearch={false}
              />
            </CreateApplicationFormField>

            <CreateApplicationFormField label="Назначение коммуникации">
              <Input name={`communications.${communicationIndex}.utilityPurpose`} placeholder="Назначение коммуникации" />
            </CreateApplicationFormField>
          </CreateApplicationFormRow>

          <CreateApplicationFormRow>
            <CreateApplicationFormField label="Вид планируемых работ" required>
              <SelectNew
                controlled
                controlledClear
                name={`communications.${communicationIndex}.plannedWorkType`}
                placeholder="Выберите из списка"
                items={toSelectItems(dictionaries.plannedWorksTypes)}
                required
                size="md"
                isSearch={false}
              />
            </CreateApplicationFormField>

            <CreateApplicationFormField label="Технология устройства пересечения">
              <Input name={`communications.${communicationIndex}.crossingTechnology`} placeholder="Например: ГНБ, прокол, продавливание" />
            </CreateApplicationFormField>
          </CreateApplicationFormRow>

          <CreateApplicationFormField label="Основные параметры коммуникации">
            <TextArea
              name={`communications.${communicationIndex}.technicalCharacteristics`}
              rows={6}
              placeholder="Укажите основные технические характеристики объекта. Например: напряжение линии электропередачи, диаметр трубопровода, пропускную способность сети, количество кабелей связи, категорию объекта и иные параметры, относящиеся к вашему типу инженерной коммуникации."
            />
          </CreateApplicationFormField>

          {(communication.intersections ?? []).map((_, intersectionIndex) => {
            const intersections = communication.intersections ?? [];

            return (
              <div key={`communication-${communicationIndex}-intersection-${intersectionIndex}`} className="flex flex-col gap-4">
                <CrossingObjectBlockHeader
                  title={`Пересечение №${intersectionIndex + 1}`}
                  onRemove={intersections.length > 1 ? () => removeIntersection(communicationIndex, intersectionIndex) : undefined}
                />

                <CreateApplicationFormRow>
                  <CrossingObjectFormField label={<CrossingObjectOptionalLabel label="Станция / Перегон" optionalHint="(если известно)" />}>
                    <Input
                      name={`communications.${communicationIndex}.intersections.${intersectionIndex}.stationSection`}
                      placeholder="Например: перегон Одинцово - Галицыно"
                    />
                  </CrossingObjectFormField>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <CrossingObjectFormField label={<CrossingObjectOptionalLabel label="Км, пикет и метр" optionalHint="(если известно)" />}>
                      <Input
                        name={`communications.${communicationIndex}.intersections.${intersectionIndex}.junctionLocation`}
                        placeholder="Например: 71 км, ПК 4+50"
                      />
                    </CrossingObjectFormField>

                    <CrossingObjectFormField label={<CrossingObjectOptionalLabel label="Координаты" optionalHint="(если известно)" />}>
                      <Input
                        name={`communications.${communicationIndex}.intersections.${intersectionIndex}.coordinates`}
                        placeholder="Например: 55.7558, 37.6176"
                      />
                    </CrossingObjectFormField>
                  </div>
                </CreateApplicationFormRow>

                <CreateApplicationFormField label="Комментарий к месту пересечения">
                  <TextArea
                    name={`communications.${communicationIndex}.intersections.${intersectionIndex}.comment`}
                    rows={6}
                    placeholder="Дополнительная информация для уточнения местоположения пересечения (при необходимости, например, ориентиры, особенности расположения, уточнения)."
                  />
                </CreateApplicationFormField>
              </div>
            );
          })}
        </div>
      ))}
    </CreateApplicationFormSection>
  );
};
